import { useEffect, useState } from 'react';
import { WifiOff, Wifi, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOnlineStatus } from '@/hooks/useServiceWorker';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';

export default function OfflineBanner() {
  const { t } = useTranslation();
  const isOnline = useOnlineStatus();
  const [showReconnected, setShowReconnected] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  // Check for queued messages when offline
  useEffect(() => {
    if (isOnline) return;

    const checkQueue = async () => {
      try {
        const request = indexedDB.open('eurocollis-outbox', 1);
        request.onsuccess = (e) => {
          const db = (e.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains('outbox')) {
            db.close();
            return;
          }
          const tx = db.transaction('outbox', 'readonly');
          const countReq = tx.objectStore('outbox').count();
          countReq.onsuccess = () => setPendingCount(countReq.result);
          tx.oncomplete = () => db.close();
        };
      } catch {
        // ignore
      }
    };

    checkQueue();
    const interval = setInterval(checkQueue, 5000);
    return () => clearInterval(interval);
  }, [isOnline]);

  useEffect(() => {
    if (!isOnline) {
      setWasOffline(true);
    } else if (wasOffline) {
      setShowReconnected(true);
      setPendingCount(0);
      const timer = setTimeout(() => {
        setShowReconnected(false);
        setWasOffline(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, wasOffline]);

  const showBanner = !isOnline || showReconnected;

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -40, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          className={cn(
            "fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-medium",
            !isOnline
              ? "bg-destructive text-destructive-foreground"
              : "bg-emerald-600 text-white"
          )}
          role="alert"
          aria-live="assertive"
        >
          {!isOnline ? (
            <>
              <WifiOff className="h-4 w-4 shrink-0" />
              <span>
                {t('offline.banner', "You're offline")}
                {pendingCount > 0 && (
                  <span className="ml-1.5 opacity-80">
                    · {t('offline.pending', '{{count}} message(s) queued', { count: pendingCount })}
                    <Loader2 className="inline h-3 w-3 ml-1 animate-spin" />
                  </span>
                )}
              </span>
            </>
          ) : (
            <>
              <Wifi className="h-4 w-4 shrink-0" />
              <span>{t('offline.reconnected', 'Back online!')}</span>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
